/**
 * Talents : un par lignée, inspirés du talent officiel du Pokémon.
 * Chaque talent = { name, desc, ...effets } ; les effets sont lus par le moteur de combat (pokeArenaData.js).
 *
 * Effets disponibles :
 *  atkMul / hpMul / spdMul / spellMul / autoMul  multiplicateurs fixes
 *  lowHp { at, atk }        bonus de dégâts sous un seuil de PV
 *  onKo                     +X% ATK à chaque KO infligé
 *  anyKo                    +X% ATK à chaque KO sur le terrain
 *  supreme                  +X% ATK par allié tombé
 *  intimidate               -X% ATK à tous les ennemis au début du combat
 *  regen                    soin X% PV max par tour
 *  allyHeal                 soigne l’allié le plus blessé de X% par tour
 *  immune [types] / absorb / immuneAtk   immunité de type (+ soin / + ATK)
 *  resist { type: mult }    dégâts reçus par type
 *  spdUp                    +X SPD par tour
 *  precharge                charges de sort au début du combat
 *  contact { chance, slow, stun, atkBreak, dot }   riposte quand on le frappe au corps à corps
 *  hitDot { chance, pct, turns }  ses attaques empoisonnent / brûlent
 *  thorns                   renvoie X% des dégâts subis
 *  magicGuard               aucun dégât de recul, de poison ou de brûlure
 *  rockHead                 aucun dégât de recul
 *  cleanse                  immunisé aux poisons / brûlures
 *  noCC                     immunisé aux ralentissements, étourdissements et baisses d’ATK
 *  fullHpGuard              dégâts reçus × X tant qu’il a tous ses PV
 *  disguise                 annule le premier coup reçu
 *  spellShield              annule le premier sort reçu
 *  protean                  ses attaques ne sont jamais résistées
 *  crit { chance, mult }    coups critiques
 *  defiant                  +X% ATK quand on baisse ses stats
 *  surge { turns, atk, spd } bonus des premiers tours (Paléo / Futur)
 *  dodge                    chance d’esquiver
 *  autoGuard / spellGuard / dmgTaken / superGuard  réductions de dégâts
 *  guts                     +X% ATK tant qu’il est sous altération
 *  pierce                   ignore les talents défensifs adverses
 *  teamAtk { mult, type? }  bonus d’ATK d’équipe au début du combat
 *  teamShield { pct, types } bouclier d’équipe au début du combat
 *  teamCleanse              équipe immunisée aux poisons / brûlures
 *  ruin                     les ennemis subissent +X% de dégâts
 *  pressure                 les ennemis lancent leur premier sort plus tard
 *  airLock                  insensible aux talents d’aura adverses
 *  neutralize               désactive les talents adverses
 *  berry                    soin unique de X% sous 50% PV
 *  powerConstruct           sous 50% PV, une fois : +50% PV max et soin
 *  vsType { type: mult }    dégâts contre un type
 *  scrappy                  touche les Spectre normalement
 *  hunger                   un tour sur deux : +40% dégâts
 *  dancer                   gagne une charge quand un allié lance un sort
 *  onHitType { type, atk?, shield? }  réaction quand touché par un type
 *  stance                   chaque sort donne un bouclier de 12%
 *  trace                    copie le talent d’un ennemi au début du combat
 *  synchro                  renvoie ralentissements et poisons à leur source
 *  badDreams                les ennemis sous altération perdent 6% PV par tour
 *  moody                    gagne un bonus aléatoire chaque tour
 *  sunPower                 perd 3% PV par tour
 *  dotMul                   multiplie ses poisons / brûlures
 *  serene                   ses sorts étourdissent en plus 30% du temps
 *  tinted                   ses coups peu efficaces font des dégâts normaux
 *  contrary                 les baisses de stats deviennent des hausses
 *  stamina                  chaque coup reçu réduit les suivants de 4% (jusqu’à -30%)
 *  onHitAtk                 chaque coup reçu donne +X% ATK
 *  onHitSpd                 chaque coup reçu donne +X SPD
 *  wonderGuard              seuls les coups super efficaces le blessent
 */

const lowHp = (name) => ({
  name,
  desc: 'Sous 1/3 de ses PV, ses dégâts augmentent de 50%.',
  lowHp: { at: 0.34, atk: 0.5 }
});

const P = {
  brasier: lowHp('Brasier'),
  torrent: lowHp('Torrent'),
  engrais: lowHp('Engrais'),
  essaim: lowHp('Essaim'),
  intimidation: { name: 'Intimidation', desc: 'Début du combat : l’ATK de tous les ennemis baisse de 15%.', intimidate: 0.15 },
  fermete: { name: 'Fermeté', desc: 'Survit une fois à un coup fatal avec 1 PV.', endure: true },
  multiecaille: { name: 'Multiécaille', desc: 'Subit 50% de dégâts en moins tant que ses PV sont au maximum.', fullHpGuard: 0.5 },
  impudence: { name: 'Impudence', desc: 'Chaque KO infligé : +20% ATK pour le reste du combat.', onKo: 0.2 },
  boostChimere: { name: 'Boost Chimère', desc: 'Chaque KO infligé : +25% ATK et +2 SPD.', onKo: 0.25, onKoSpd: 2 },
  regeForce: { name: 'Régé-Force', desc: 'Récupère 4% de ses PV max à chaque tour.', regen: 0.04 },
  levitation: { name: 'Lévitation', desc: 'Immunisé aux attaques de type Sol.', immune: ['Sol'] },
  absorbeVolt: { name: 'Absorb Volt', desc: 'Immunisé à Électrik ; un coup Électrik le soigne de 25%.', immune: ['Électrik'], absorb: 0.25 },
  absorbeEau: { name: 'Absorbe-Eau', desc: 'Immunisé à Eau ; un coup Eau le soigne de 25%.', immune: ['Eau'], absorb: 0.25 },
  torche: { name: 'Torche', desc: 'Immunisé à Feu ; un coup Feu lui donne +25% ATK.', immune: ['Feu'], immuneAtk: 0.25 },
  paratonnerre: { name: 'Paratonnerre', desc: 'Immunisé à Électrik ; un coup Électrik lui donne +20% ATK.', immune: ['Électrik'], immuneAtk: 0.2 },
  peauSeche: { name: 'Peau Sèche', desc: 'Immunisé à Eau (soin 20%), mais subit +25% des attaques Feu.', immune: ['Eau'], absorb: 0.2, resist: { Feu: 1.25 } },
  isograisse: { name: 'Isograisse', desc: 'Subit moitié moins de dégâts des attaques Feu et Glace.', resist: { Feu: 0.5, Glace: 0.5 } },
  turbo: { name: 'Turbo', desc: '+2 SPD à chaque tour.', spdUp: 2 },
  farceur: { name: 'Farceur', desc: 'Commence le combat avec 2 charges de sort : son premier sort part très vite.', precharge: 2 },
  statik: { name: 'Statik', desc: 'Frappé au corps à corps : 30% d’étourdir l’attaquant.', contact: { chance: 0.3, stun: true } },
  corpsArdent: { name: 'Corps Ardent', desc: 'Frappé au corps à corps : 30% de brûler l’attaquant (5%/tour, 3 tours).', contact: { chance: 0.3, dot: { pct: 0.05, turns: 3 } } },
  pointPoison: { name: 'Point Poison', desc: 'Frappé au corps à corps : 30% d’empoisonner l’attaquant (5%/tour, 3 tours).', contact: { chance: 0.3, dot: { pct: 0.05, turns: 3 } } },
  peauDure: { name: 'Peau Dure', desc: 'Renvoie 15% des dégâts subis à l’attaquant.', thorns: 0.15 },
  gardeMagik: { name: 'Garde Magik', desc: 'Ne subit que les dégâts directs : ni recul, ni poison, ni brûlure.', magicGuard: true, rockHead: true, cleanse: true },
  illusion: { name: 'Illusion', desc: 'Le premier coup reçu ne fait aucun dégât (l’illusion se brise).', disguise: true },
  deguisement: { name: 'Déguisement', desc: 'Le premier coup reçu ne fait aucun dégât (le déguisement se brise).', disguise: true },
  proteen: { name: 'Protéen', desc: 'Change de type avant d’attaquer : ses coups ne sont jamais résistés.', protean: true },
  sniper: { name: 'Sniper', desc: '25% de coups critiques, qui infligent ×1,8.', crit: { chance: 0.25, mult: 1.8 } },
  chanceux: { name: 'Chanceux', desc: '30% de coups critiques (×1,5).', crit: { chance: 0.3, mult: 1.5 } },
  colerique: { name: 'Colérique', desc: 'Sous 50% de ses PV, ses dégâts augmentent de 35%.', lowHp: { at: 0.5, atk: 0.35 } },
  acharne: { name: 'Acharné', desc: 'Chaque fois qu’on baisse ses stats : +25% ATK.', defiant: 0.25 },
  generalSupreme: { name: 'Général Suprême', desc: '+12% ATK pour chaque allié mis KO.', supreme: 0.12 },
  paleosynthese: { name: 'Paléosynthèse', desc: 'Les 4 premiers tours : +30% ATK.', surge: { turns: 4, atk: 0.3 } },
  chargeQuantique: { name: 'Charge Quantique', desc: 'Les 4 premiers tours : +20% ATK et +5 SPD.', surge: { turns: 4, atk: 0.2, spd: 5 } },
  piedsConfus: { name: 'Pieds Confus', desc: '20% de chances d’esquiver chaque attaque.', dodge: 0.2 },
  toisonEpaisse: { name: 'Toison Épaisse', desc: 'Subit 40% de dégâts en moins des attaques normales (hors sorts).', autoGuard: 0.6 },
  cran: { name: 'Cran', desc: 'Tant qu’il est ralenti, empoisonné ou brûlé : +40% ATK.', guts: 0.4 },
  medicNature: { name: 'Médic Nature', desc: 'Immunisé aux poisons et brûlures.', cleanse: true },
  anticipation: { name: 'Anticipation', desc: 'Pressent le danger : le premier sort reçu est esquivé.', spellShield: true },
  miroirMagik: { name: 'Miroir Magik', desc: 'Le premier sort reçu est annulé.', spellShield: true },
  briseMoule: { name: 'Brise Moule', desc: 'Ignore les talents défensifs adverses (immunités, esquive, réductions…).', pierce: true },
  infiltration: { name: 'Infiltration', desc: 'Passe à travers les talents défensifs et les boucliers adverses.', pierce: true },
  annuleGarde: { name: 'Annule Garde', desc: 'Ses coups ignorent esquives et talents défensifs.', pierce: true },
  adaptabilite: { name: 'Adaptabilité', desc: '+20% de dégâts sur toutes ses attaques.', atkMul: 1.2 },
  technicien: { name: 'Technicien', desc: 'Ses attaques normales (hors sorts) font +35% de dégâts.', autoMul: 1.35 },
  poingDeFer: { name: 'Poing de Fer', desc: 'Ses sorts font +20% de dégâts.', spellMul: 1.2 },
  sansLimite: { name: 'Sans Limite', desc: 'Ses sorts font +30% de dégâts.', spellMul: 1.3 },
  tranchant: { name: 'Tranchant', desc: 'Ses sorts tranchants font +25% de dégâts.', spellMul: 1.25 },
  prognathe: { name: 'Prognathe', desc: 'Ses morsures (sorts) font +30% de dégâts.', spellMul: 1.3 },
  forceSoleil: { name: 'Force Soleil', desc: 'Ses sorts font +35% de dégâts, mais il perd 3% PV par tour.', spellMul: 1.35, sunPower: true },
  chlorophylle: { name: 'Chlorophylle', desc: '+30% SPD.', spdMul: 1.3 },
  glissade: { name: 'Glissade', desc: '+30% SPD.', spdMul: 1.3 },
  baigneSable: { name: 'Baigne Sable', desc: '+30% SPD.', spdMul: 1.3 },
  surfCaudal: { name: 'Surf Caudal', desc: '+30% SPD.', spdMul: 1.3 },
  armurbaston: { name: 'Armurbaston', desc: 'Subit 15% de dégâts en moins et ne prend jamais de coup critique.', dmgTaken: 0.85, noCrit: true },
  solideRoc: { name: 'Solide Roc', desc: 'Les coups super efficaces lui font 25% de dégâts en moins.', superGuard: 0.75 },
  ecailleGlace: { name: 'Écaille Glace', desc: 'Les sorts lui font 40% de dégâts en moins.', spellGuard: 0.6 },
  pareBalles: { name: 'Pare-Balles', desc: 'Les sorts lui font 35% de dégâts en moins.', spellGuard: 0.65 },
  poisseux: { name: 'Poisseux', desc: 'Frappé au corps à corps : l’attaquant perd 3 SPD.', contact: { chance: 1, slow: 3 } },
  corpsGel: { name: 'Corps Gel', desc: 'Frappé au corps à corps : 35% de geler (étourdir) l’attaquant.', contact: { chance: 0.35, stun: true } },
  corpsMaudit: { name: 'Corps Maudit', desc: 'Frappé au corps à corps : 40% de baisser l’ATK de l’attaquant de 15%.', contact: { chance: 0.4, atkBreak: 0.15 } },
  joliSourire: { name: 'Joli Sourire', desc: 'Frappé au corps à corps : 30% de charmer (baisse l’ATK de 12%).', contact: { chance: 0.3, atkBreak: 0.12 } },
  debrisToxiques: { name: 'Débris Toxiques', desc: 'Frappé au corps à corps : 40% d’empoisonner l’attaquant.', contact: { chance: 0.4, dot: { pct: 0.05, turns: 3 } } },
  toxitouche: { name: 'Toxitouche', desc: 'Ses attaques ont 30% d’empoisonner (5%/tour, 3 tours).', hitDot: { chance: 0.3, pct: 0.05, turns: 3 } },
  corrosion: { name: 'Corrosion', desc: 'Ses poisons font deux fois plus de dégâts.', dotMul: 2 },
  sableHumide: { name: 'Sable Humide', desc: 'Touché par une attaque Eau : gagne un bouclier de 20% PV.', onHitType: { type: 'Eau', shield: 0.2 } },
  coeurNoble: { name: 'Cœur Noble', desc: 'Touché par une attaque Ténèbres : +20% ATK.', onHitType: { type: 'Ténèbres', atk: 0.2 } },
  danseuse: { name: 'Danseuse', desc: 'Chaque sort lancé par un allié lui donne une charge de sort.', dancer: true },
  pression: { name: 'Pression', desc: 'Les ennemis lancent leur premier sort un tour plus tard.', pressure: 1 },
  ecranPoudre: { name: 'Écran Poudre', desc: 'Immunisé aux ralentissements, étourdissements et baisses d’ATK.', noCC: true },
  corpsSain: { name: 'Corps Sain', desc: 'Immunisé aux ralentissements, étourdissements et baisses d’ATK.', noCC: true },
  miroirArmure: { name: 'Miroir Armure', desc: 'Immunisé aux ralentissements, étourdissements et baisses d’ATK.', noCC: true },
  attention: { name: 'Attention', desc: 'Immunisé aux étourdissements, ralentissements et à l’Intimidation.', noCC: true, airLock: true },
  corpsEnOr: { name: 'Corps en Or', desc: 'Immunisé à toutes les altérations (ralentissement, étourdissement, poison…).', noCC: true, cleanse: true },
  teteDeRoc: { name: 'Tête de Roc', desc: 'Ne subit jamais de dégâts de recul (Orbe Vie, Casque Brut…).', rockHead: true },
  gloutonnerie: { name: 'Gloutonnerie', desc: 'Sous 50% PV, mange sa baie : soigne 30% une fois.', berry: 0.3 },
  recolte: { name: 'Récolte', desc: 'Récupère 4% de ses PV par tour et soigne 25% une fois sous 50% PV.', regen: 0.04, berry: 0.25 },
  magnepiege: { name: 'Magnépiège', desc: '+35% de dégâts contre les Pokémon Acier.', vsType: { Acier: 1.35 } },
  querelleur: { name: 'Querelleur', desc: 'Ses coups touchent normalement les Spectre.', scrappy: true },
  declicFringale: { name: 'Déclic Fringale', desc: 'Un tour sur deux (mode Rassasié) : +40% de dégâts.', hunger: 0.4 },
  rassemblement: { name: 'Rassemblement', desc: 'Une fois sous 50% PV : +50% PV max et se soigne d’autant.', powerConstruct: 0.5 },
  coeurDeCoq: { name: 'Cœur de Coq', desc: 'Chaque KO sur le terrain (allié ou ennemi) : +10% ATK.', anyKo: 0.1 },
  serenite: { name: 'Sérénité', desc: 'Ses sorts ont en plus 30% de chances d’étourdir.', serene: 0.3 },
  victorieux: { name: 'Victorieux', desc: 'Début du combat : +10% ATK à toute l’équipe.', teamAtk: { mult: 0.1 } },
  alerteNeige: { name: 'Alerte Neige', desc: 'Début du combat : +15% ATK aux alliés Glace.', teamAtk: { mult: 0.15, type: 'Glace' } },
  secheresse: { name: 'Sécheresse', desc: 'Début du combat : +15% ATK aux alliés Feu.', teamAtk: { mult: 0.15, type: 'Feu' } },
  moteurHadron: { name: 'Moteur à Hadrons', desc: 'Début du combat : +15% ATK aux alliés Électrik.', teamAtk: { mult: 0.15, type: 'Électrik' } },
  auraFeerique: { name: 'Aura Féérique', desc: 'Début du combat : +15% ATK aux alliés Fée.', teamAtk: { mult: 0.15, type: 'Fée' } },
  auraTenebreuse: { name: 'Aura Ténébreuse', desc: 'Début du combat : +15% ATK aux alliés Ténèbres.', teamAtk: { mult: 0.15, type: 'Ténèbres' } },
  sableVolant: { name: 'Sable Volant', desc: 'Début du combat : bouclier de 12% PV aux alliés Roche, Sol et Acier.', teamShield: { pct: 0.12, types: ['Roche', 'Sol', 'Acier'] } },
  creaBrume: { name: 'Créa-Brume', desc: 'Toute l’équipe est immunisée aux poisons et brûlures.', teamCleanse: true },
  voileSucre: { name: 'Voile Sucre', desc: 'Soigne l’allié le plus blessé de 5% à chaque tour.', allyHeal: 0.05 },
  coeurSoin: { name: 'Cœur Soin', desc: 'Soigne l’allié le plus blessé de 5% à chaque tour.', allyHeal: 0.05 },
  epeeFleau: { name: 'Épée du Fléau', desc: 'Tous les ennemis subissent +12% de dégâts.', ruin: 0.12 },
  gazInhibiteur: { name: 'Gaz Inhibiteur', desc: 'Désactive les talents de l’équipe adverse.', neutralize: true },
  aerolock: { name: 'Air Lock', desc: 'Insensible à l’Intimidation, à la Pression et aux fléaux adverses.', airLock: true },
  masque: { name: 'Hospitalité masquée', desc: 'Le masque porté donne +15% ATK et +15% SPD.', atkMul: 1.15, spdMul: 1.15 },
  lameIndomptable: { name: 'Lame Indomptable', desc: 'Entre en combat lame au clair : +25% ATK.', atkMul: 1.25 },
  coloforce: { name: 'Coloforce', desc: 'Force herculéenne : +35% ATK.', atkMul: 1.35 },
  punkRock: { name: 'Punk Rock', desc: 'Ses sorts font +30% de dégâts ; les sorts reçus font 25% de moins.', spellMul: 1.3, spellGuard: 0.75 },
  declicTactique: { name: 'Déclic Tactique', desc: 'Chaque sort lancé le protège d’un bouclier de 12% PV.', stance: 0.12 },
  soinPoison: { name: 'Soin Poison', desc: 'Immunisé au poison ; récupère 5% PV par tour.', cleanse: true, regen: 0.05 },
  calque: { name: 'Calque', desc: 'Début du combat : copie le talent d’un ennemi au hasard.', trace: true },
  synchro: { name: 'Synchro', desc: 'Renvoie à leur source les ralentissements et poisons subis.', synchro: true },
  mauvaisReve: { name: 'Mauvais Rêve', desc: 'Les ennemis ralentis, étourdis ou empoisonnés perdent 6% PV par tour.', badDreams: 0.06 },
  lunatique: { name: 'Lunatique', desc: 'Chaque tour : gagne au hasard +8% ATK ou +2 SPD.', moody: true },
  ailesBourrasque: { name: 'Ailes Bourrasque', desc: 'Commence avec 2 charges de sort : il frappe en premier.', precharge: 2 },
  echangeThermique: { name: 'Échange Thermique', desc: 'Immunisé aux brûlures ; touché par Feu : +20% ATK.', cleanse: true, onHitType: { type: 'Feu', atk: 0.2 } },
  delestage: { name: 'Délestage', desc: '+30% SPD.', spdMul: 1.3 },
  bouleDePoils: { name: 'Boule de Poils', desc: 'Subit 40% de dégâts en moins des attaques normales (hors sorts).', autoGuard: 0.6 },
  regardVif: { name: 'Regard Vif', desc: 'Ses coups ignorent l’esquive et les talents défensifs.', pierce: true },
  lentiteintee: { name: 'Lentiteintée', desc: 'Ses coups peu efficaces font quand même des dégâts normaux.', tinted: true },
  lavabo: { name: 'Lavabo', desc: 'Immunisé à Eau ; un coup Eau lui donne +20% ATK.', immune: ['Eau'], immuneAtk: 0.2 },
  hydratation: { name: 'Hydratation', desc: 'Immunisé aux poisons et brûlures.', cleanse: true },
  griffeDure: { name: 'Griffe Dure', desc: '+20% de dégâts sur toutes ses attaques.', atkMul: 1.2 },
  contestataire: { name: 'Contestataire', desc: 'Les baisses d’ATK et de SPD qu’il subit deviennent des hausses.', contrary: true },
  epineDeFer: { name: 'Épine de Fer', desc: 'Renvoie 15% des dégâts subis à l’attaquant.', thorns: 0.15 },
  receveur: { name: 'Receveur', desc: '+12% ATK pour chaque allié mis KO.', supreme: 0.12 },
  endurance: { name: 'Endurance', desc: 'Chaque coup reçu réduit les dégâts suivants de 4% (jusqu’à -30%).', stamina: true },
  creaHerbe: { name: 'Créa-Herbe', desc: 'Soigne l’allié le plus blessé de 5% à chaque tour.', allyHeal: 0.05 },
  creaPsy: { name: 'Créa-Psy', desc: 'Début du combat : +12% ATK aux alliés Psy.', teamAtk: { mult: 0.12, type: 'Psy' } },
  pointEnergie: { name: 'Point Énergie', desc: 'Début du combat : +8% ATK à toute l’équipe.', teamAtk: { mult: 0.08 } },
  chargeRocheuse: { name: 'Charge Rocheuse', desc: 'Ses sorts font +25% de dégâts.', spellMul: 1.25 },
  filature: { name: 'Filature', desc: 'Les 2 premiers tours : +50% de dégâts.', surge: { turns: 2, atk: 0.5 } },
  selPurificateur: { name: 'Sel Purificateur', desc: 'Immunisé aux poisons et brûlures ; les attaques Spectre lui font moitié moins mal.', cleanse: true, resist: { Spectre: 0.5 } },
  electromorphose: { name: 'Électromorphose', desc: 'Chaque coup reçu : +6% ATK.', onHitAtk: 0.06 },
  mycelium: { name: 'Force Mycélium', desc: 'Ses coups ignorent les talents défensifs adverses.', pierce: true },
  libero: { name: 'Libéro', desc: 'Change de type avant d’attaquer : ses coups ne sont jamais résistés.', protean: true },
  rideauNeige: { name: 'Rideau Neige', desc: '20% de chances d’esquiver chaque attaque.', dodge: 0.2 },
  peauFeerique: { name: 'Peau Féérique', desc: 'Ses attaques deviennent féériques : +20% de dégâts.', atkMul: 1.2 },
  agitation: { name: 'Agitation', desc: '+25% de dégâts sur toutes ses attaques.', atkMul: 1.25 },
  gardeMystik: { name: 'Garde Mystik', desc: 'Seuls les coups super efficaces le blessent.', wonderGuard: true },
  impassible: { name: 'Impassible', desc: 'Immunisé aux altérations de stats et gagne +1 SPD par tour.', noCC: true, spdUp: 1 },
  insomnie: { name: 'Insomnie', desc: 'Immunisé aux étourdissements ; ses sorts font +15% de dégâts.', noCC: true, spellMul: 1.15 },
  armurfragile: { name: 'Armurfragile', desc: 'Chaque coup reçu : +2 SPD.', onHitSpd: 2 },
  temeraire: { name: 'Téméraire', desc: 'Ses sorts font +25% de dégâts.', spellMul: 1.25 },
  chasseNeige: { name: 'Chasse-Neige', desc: '+30% SPD.', spdMul: 1.3 },
  peauElectrik: { name: 'Peau Électrique', desc: 'Ses attaques s’électrisent : +20% de dégâts.', atkMul: 1.2 },
  competitif: { name: 'Compétitif', desc: 'Chaque fois qu’on baisse ses stats : +30% ATK.', defiant: 0.3 },
  teraVoltage: { name: 'Téra-Voltage', desc: 'Ses coups ignorent les talents défensifs adverses.', pierce: true },
  turbobrasier: { name: 'Turbobrasier', desc: 'Ses coups ignorent les talents défensifs adverses.', pierce: true },
  pasDeDanse: { name: 'Pas de Danse', desc: '+20% SPD et +20% de dégâts sur ses attaques normales.', spdMul: 1.2, autoMul: 1.2 },
  oeilRevelateur: { name: 'Œil Révélateur', desc: 'Touche normalement les Spectre et ignore l’esquive.', scrappy: true, pierce: true },
  metalloGarde: { name: 'Métallo-Garde', desc: 'Immunisé aux ralentissements, étourdissements et baisses d’ATK.', noCC: true },
  spectroBouclier: { name: 'Spectro-Bouclier', desc: 'Subit 50% de dégâts en moins tant que ses PV sont au maximum.', fullHpGuard: 0.5 },
  masquePuits: { name: 'Masque du Puits', desc: 'Immunisé à Eau (soin 25%) ; +15% ATK.', immune: ['Eau'], absorb: 0.25, atkMul: 1.15 },
  masqueFourneau: { name: 'Masque du Fourneau', desc: 'Ses coups ignorent les talents défensifs ; +15% ATK.', pierce: true, atkMul: 1.15 },
  masquePierre: { name: 'Masque de la Pierre', desc: 'Survit une fois à 1 PV ; +15% PV.', endure: true, hpMul: 1.15 },
  crachin: { name: 'Crachin', desc: 'Début du combat : +12% ATK aux alliés Eau.', teamAtk: { mult: 0.12, type: 'Eau' } },
  forcePure: { name: 'Force Pure', desc: '+30% ATK.', atkMul: 1.3 },
  motorise: { name: 'Motorisé', desc: 'Immunisé à Électrik ; gagne 1 SPD à chaque tour.', immune: ['Électrik'], spdUp: 1 },
  poingInvisibleFinal: { name: 'Poing Invisible', desc: 'Ignore boucliers et talents défensifs ; 25% de coups critiques (×1,6).', pierce: true, crit: { chance: 0.25, mult: 1.6 } },
  poingInvisibleMille: { name: 'Poing Invisible', desc: 'Ignore boucliers et talents défensifs ; +20% SPD.', pierce: true, spdMul: 1.2 },
  commandant: { name: 'Commandant', desc: 'Début du combat : +12% ATK aux alliés Dragon.', teamAtk: { mult: 0.12, type: 'Dragon' } },
  multiType: { name: 'Multi-Type', desc: 'Ses coups ne sont jamais résistés.', protean: true },
  pulsOrichalque: { name: 'Pouls Orichalque', desc: 'Début du combat : +15% ATK aux alliés Combat.', teamAtk: { mult: 0.15, type: 'Combat' } }
};


/** Talent de chaque lignée (talent officiel du Pokémon, adapté au combat auto). */
export const TALENTS = {
  eevee: P.adaptabilite,
  fletch: P.ailesBourrasque,
  mudkip: P.torrent,
  shroom: P.technicien,
  magikarp: P.intimidation,
  litwick: P.infiltration,
  bidoof: P.lunatique,
  wooper: P.absorbeEau,
  mareep: P.statik,
  azurill: P.coloforce,
  rowlet: P.engrais,
  cynda: P.brasier,
  chinchou: P.absorbeVolt,
  gastly: P.levitation,
  scyther: P.technicien,
  sneasel: P.pression,
  riolu: P.coeurNoble,
  mankey: P.acharne,
  growlithe: P.intimidation,
  abra: P.gardeMagik,
  trapinch: P.levitation,
  roserade: P.pointPoison,
  ralts: P.calque,
  toxel: P.punkRock,
  'zorua-h': P.illusion,
  honedge: P.declicTactique,
  gligar: P.soinPoison,
  spheal: P.isograisse,
  mimikyu: P.deguisement,
  noibat: P.infiltration,
  tinkatink: P.briseMoule,
  misdreavus: P.levitation,
  charcadet: P.torche,
  dreepy: P.infiltration,
  rookidee: P.miroirArmure,
  dratini: P.multiecaille,
  beldum: P.corpsSain,
  tyrunt: P.prognathe,
  pawniard: P.generalSupreme,
  gible: P.peauDure,
  larvesta: P.corpsArdent,
  frigibax: P.echangeThermique,
  froakie: P.proteen,
  ironval: P.chargeQuantique,
  celebi: P.medicNature,
  dialga: P.pression,
  mew: P.synchro,
  pheromosa: P.boostChimere,
  darkrai: P.mauvaisReve,
  lugia: P.multiecaille,
  axew: P.briseMoule,
  charmander: P.forceSoleil,
  sprigatito: P.proteen,
  torchic: P.turbo,
  totodile: P.sansLimite,
  snivy: P.contestataire,
  grubbin: P.levitation,
  cubone: P.teteDeRoc,
  clodsire: P.absorbeEau,
  gholdengo: P.corpsEnOr,
  lilligant: P.chlorophylle,
  drifloon: P.delestage,
  sableye: P.farceur,
  mawile: P.coloforce,
  absol: P.chanceux,
  aron: P.fermete,
  bagon: P.intimidation,
  deino: P.levitation,
  shaymin: P.medicNature,
  zapdos: P.pression,
  rayquaza: P.aerolock,
  raichu: P.statik,
  spinda: P.piedsConfus,
  greavard: P.bouleDePoils,
  cetoddle: P.isograisse,
  capsakid: P.chlorophylle,
  flamigo: P.querelleur,
  applin: P.gloutonnerie,
  bulbasaur: P.engrais,
  squirtle: P.torrent,
  'chien-pao': P.epeeFleau,
  'iron-hands': P.chargeQuantique,
  lapras: P.absorbeEau,
  exegg: P.chlorophylle,
  articuno: P.pression,
  machop: P.cran,
  nido: P.sansLimite,
  sand: P.baigneSable,
  pidgey: P.regardVif,
  pinsir: P.briseMoule,
  geodude: P.fermete,
  magnemite: P.magnepiege,
  clefairy: P.gardeMagik,
  mewtwo: P.pression,
  sentret: P.regardVif,
  chikorita: P.engrais,
  swinub: P.isograisse,
  tyrogue: P.technicien,
  zubat: P.infiltration,
  hoothoot: P.lentiteintee,
  shuckle: P.fermete,
  horsea: P.sniper,
  larvitar: P.sableVolant,
  onix: P.fermete,
  togepi: P.serenite,
  treecko: P.engrais,
  electrike: P.paratonnerre,
  snorunt: P.corpsGel,
  taillow: P.cran,
  nincada: P.turbo,
  jirachi: P.serenite,
  buneary: P.joliSourire,
  chimchar: P.poingDeFer,
  piplup: P.torrent,
  turtwig: P.engrais,
  shinx: P.intimidation,
  shellos: P.lavabo,
  combee: P.pression,
  cranidos: P.briseMoule,
  spiritomb: P.infiltration,
  skorupi: P.armurbaston,
  giratina: P.pression,
  minccino: P.technicien,
  tepig: P.brasier,
  oshawott: P.tranchant,
  blitzle: P.paratonnerre,
  cubchoo: P.glissade,
  timburr: P.cran,
  venipede: P.pointPoison,
  drilbur: P.baigneSable,
  ducklett: P.hydratation,
  munna: P.anticipation,
  sewaddle: P.essaim,
  roggenrola: P.sableVolant,
  karrablast: P.armurbaston,
  cottonee: P.farceur,
  kyurem: P.pression,
  meloetta: P.serenite,
  fennekin: P.brasier,
  chespin: P.pareBalles,
  helioptile: P.peauSeche,
  bergmite: P.fermete,
  hawlucha: P.delestage,
  skrelp: P.adaptabilite,
  binacle: P.griffeDure,
  espurr: P.farceur,
  goomy: P.poisseux,
  inkay: P.contestataire,
  klefki: P.farceur,
  yveltal: P.auraTenebreuse,
  diancie: P.corpsSain,
  stufful: P.bouleDePoils,
  litten: P.intimidation,
  popplio: P.torrent,
  togedemaru: P.epineDeFer,
  crabrawler: P.poingDeFer,
  passimian: P.receveur,
  salandit: P.corrosion,
  mudbray: P.endurance,
  oricorio: P.danseuse,
  oranguru: P.attention,
  rockruff: P.annuleGarde,
  jangmo: P.pareBalles,
  fomantis: P.contestataire,
  lunala: P.multiecaille,
  marshadow: P.technicien,
  'grimer-a': P.toxitouche,
  scorbunny: P.libero,
  sobble: P.sniper,
  grookey: P.creaHerbe,
  snom: P.ecailleGlace,
  falinks: P.armurbaston,
  'zigzagoon-g': P.acharne,
  'yamask-g': P.corpsMaudit,
  'corsola-g': P.corpsMaudit,
  'meowth-g': P.griffeDure,
  impidimp: P.farceur,
  hatenna: P.miroirMagik,
  blipbug: P.essaim,
  indeedee: P.creaPsy,
  stonjourner: P.pointEnergie,
  eternatus: P.pression,
  zarude: P.medicNature,
  girafarig: P.gloutonnerie,
  fuecoco: P.brasier,
  quaxly: P.impudence,
  starly: P.intimidation,
  milcery: P.voileSucre,
  furfrou: P.toisonEpaisse,
  vivillon: P.ecranPoudre,
  ogerpon: P.masque,
  miraidon: P.moteurHadron,
  roaring: P.paleosynthese,
  toedscool: P.mycelium,
  bombirdier: P.chargeRocheuse,
  tarountula: P.filature,
  nacli: P.selPurificateur,
  bellibolt: P.electromorphose,
  teddiursa: P.cran,
  swablu: P.medicNature,
  chatot: P.piedsConfus,
  lillipup: P.querelleur,
  drampa: P.colerique,
  victini: P.victorieux,
  remoraid: P.sniper,
  barboach: P.anticipation,
  keldeo: P.coeurNoble,
  'tapu-fini': P.creaBrume,
  pansage: P.gloutonnerie,
  phantump: P.recolte,
  tynamo: P.levitation,
  zeraora: P.absorbeVolt,
  'raging-bolt': P.paleosynthese,
  amaura: P.alerteNeige,
  'vulpix-a': P.alerteNeige,
  'iron-bundle': P.chargeQuantique,
  pancham: P.briseMoule,
  'farfetchd-g': P.chanceux,
  mareanie: P.regeForce,
  zygarde: P.rassemblement,
  sandygast: P.sableHumide,
  golett: P.poingDeFer,
  aerodactyl: P.teteDeRoc,
  landorus: P.sansLimite,
  solosis: P.gardeMagik,
  'scream-tail': P.paleosynthese,
  wurmple: P.essaim,
  'slither-wing': P.paleosynthese,
  kabuto: P.glissade,
  rhyhorn: P.solideRoc,
  terrakion: P.coeurNoble,
  glimmet: P.debrisToxiques,
  shuppet: P.corpsMaudit,
  zorua: P.illusion,
  morpeko: P.declicFringale,
  magearna: P.coeurDeCoq,
  igglybuff: P.joliSourire,
  xerneas: P.auraFeerique,
  koffing: P.gazInhibiteur,
  munchlax: P.isograisse,
  groudon: P.secheresse,
  zacian: P.lameIndomptable,
  caterpie: P.ecranPoudre,
  rattata: P.cran,
  psyduck: P.aerolock,
  poliwag: P.absorbeEau,
  vulpix: P.torche,
  ponyta: P.torche,
  krabby: P.armurbaston,
  pachirisu: P.absorbeVolt,
  poochyena: P.intimidation,
  lotad: P.glissade,
  seedot: P.chlorophylle,
  pawmi: P.poingDeFer,
  fidough: P.impassible,
  slowpoke: P.regeForce,
  ekans: P.intimidation,
  houndour: P.torche,
  phanpy: P.fermete,
  carvanha: P.turbo,
  croagunk: P.peauSeche,
  hippopotas: P.sableVolant,
  nickit: P.filature,
  yungoos: P.prognathe,
  sinistea: P.armurfragile,
  bounsweet: P.voileSucre,
  wimpod: P.endurance,
  numel: P.solideRoc,
  chingling: P.levitation,
  elekid: P.motorise,
  magby: P.corpsArdent,
  porygon: P.adaptabilite,
  feebas: P.competitif,
  duskull: P.pression,
  rolycoly: P.torche,
  flabebe: P.coeurSoin,
  clamperl: P.armurbaston,
  mienfoo: P.temeraire,
  meditite: P.forcePure,
  staryu: P.medicNature,
  litleo: P.impudence,
  duraludon: P.endurance,
  kubfu: P.attention,
  kangaskhan: P.querelleur,
  tatsugiri: P.commandant,
  'ho-oh': P.regeForce,
  suicune: P.pression,
  entei: P.attention,
  raikou: P.pression,
  latias: P.levitation,
  latios: P.levitation,
  kyogre: P.crachin,
  palkia: P.pression,
  reshiram: P.turbobrasier,
  zekrom: P.teraVoltage,
  arceus: P.multiType,
  koraidon: P.pulsOrichalque,
  heatran: P.torche,
  // Vague 3
  weedle: P.essaim,
  oddish: P.chlorophylle,
  bellsprout: P.chlorophylle,
  whismur: P.punkRock,
  pidove: P.regardVif,
  pikipek: P.technicien,
  wooloo: P.toisonEpaisse,
  tandemaus: P.technicien,
  skwovet: P.gloutonnerie,
  lechonk: P.gloutonnerie,
  kricketot: P.essaim,
  hoppip: P.chlorophylle,
  seel: P.isograisse,
  smoliv: P.creaHerbe,
  buizel: P.glissade,
  tentacool: P.corpsSain,
  shellder: P.technicien,
  drowzee: P.insomnie,
  natu: P.miroirMagik,
  snubbull: P.intimidation,
  sandile: P.intimidation,
  tympole: P.glissade,
  elgyem: P.synchro,
  rufflet: P.temeraire,
  vullaby: P.armurfragile,
  skiddo: P.medicNature,
  pumpkaboo: P.recolte,
  wattrel: P.electromorphose,
  maschiff: P.intimidation,
  shroodle: P.toxitouche,
  nymble: P.temeraire,
  snover: P.alerteNeige,
  dewpider: P.hydratation,
  slakoth: P.forcePure,
  happiny: P.coeurSoin,
  vanillite: P.rideauNeige,
  tangela: P.regeForce,
  stantler: P.intimidation,
  klink: P.corpsSain,
  sizzlipede: P.torche,
  yanma: P.turbo,
  lickitung: P.impassible,
  dunsparce: P.serenite,
  archen: P.temeraire,
  tirtouga: P.solideRoc,
  shieldon: P.fermete,
  anorith: P.armurbaston,
  omanyte: P.glissade,
  lileep: P.lavabo,
  cufant: P.forcePure,
  clauncher: P.sniper,
  varoom: P.motorise,
  bronzor: P.levitation,
  tauros: P.intimidation,
  'great-tusk': P.paleosynthese,
  'flutter-mane': P.paleosynthese,
  'iron-treads': P.chargeQuantique,
  'iron-moth': P.chargeQuantique,
  'iron-jugulis': P.chargeQuantique,
  'sandy-shocks': P.paleosynthese,
  nihilego: P.boostChimere,
  buzzwole: P.boostChimere,
  kartana: P.boostChimere,
  xurkitree: P.boostChimere,
  poipole: P.boostChimere,
  'type-null': P.adaptabilite,
  uxie: P.levitation,
  mesprit: P.levitation,
  azelf: P.levitation,
  cresselia: P.levitation,
  regirock: P.corpsSain,
  regice: P.corpsSain,
  registeel: P.corpsSain,
  okidogi: P.toxitouche,
  munkidori: P.toxitouche,
  fezandipiti: P.toxitouche,
  regigigas: P.forcePure,
  zamazenta: P.lameIndomptable,
  volcanion: P.absorbeEau,
  necrozma: P.multiecaille,
  deoxys: P.pression,
  genesect: P.adaptabilite,
  hoopa: P.gardeMagik,
  moltres: P.pression,
  cobalion: P.coeurNoble,
  virizion: P.coeurNoble,
  tornadus: P.farceur,
  thundurus: P.farceur,
  'chi-yu': P.epeeFleau,
  'ting-lu': P.epeeFleau,
  'wo-chien': P.epeeFleau,
  'tapu-koko': P.peauElectrik,
  'tapu-lele': P.creaPsy,
  'tapu-bulu': P.creaHerbe,
  pecharunt: P.toxitouche,
  meltan: P.fermete,
  calyrex: P.gardeMagik,
  'walking-wake': P.paleosynthese,
  'iron-leaves': P.chargeQuantique
};

/** Talents propres à une forme (évolutions à choix) : clé « lignée:forme ». */
export const FORM_TALENTS = {
  'eevee:aque': P.absorbeEau,
  'eevee:volt': P.absorbeVolt,
  'eevee:pyro': P.torche,
  'eevee:ment': P.miroirMagik,
  'eevee:noct': P.synchro,
  'eevee:phyl': P.chlorophylle,
  'eevee:givr': P.rideauNeige,
  'eevee:nym': P.peauFeerique,
  'rowlet:alola': P.engrais,
  'rowlet:hisui': P.querelleur,
  'cynda:johto': P.torche,
  'cynda:hisui': P.brasier,
  'scyther:scizor': P.technicien,
  'scyther:kleavor': P.tranchant,
  'sneasel:johto': P.pression,
  'sneasel:hisui': P.toxitouche,
  'growlithe:kanto': P.intimidation,
  'growlithe:hisui': P.teteDeRoc,
  'ralts:garde': P.calque,
  'ralts:gall': P.tranchant,
  'toxel:aigue': P.punkRock,
  'toxel:grave': P.technicien,
  'charcadet:arma': P.torche,
  'charcadet:ceru': P.armurfragile,
  'zapdos:kanto': P.pression,
  'zapdos:galar': P.acharne,
  'raichu:kanto': P.statik,
  'raichu:alola': P.surfCaudal,
  'applin:flapple': P.agitation,
  'applin:appletun': P.isograisse,
  'applin:dipplin': P.regeForce,
  'cubone:kanto': P.teteDeRoc,
  'cubone:alola': P.corpsMaudit,
  'lilligant:unova': P.chlorophylle,
  'lilligant:hisui': P.agitation,
  'exegg:kanto': P.chlorophylle,
  'exegg:alola': P.recolte,
  'articuno:kanto': P.pression,
  'articuno:galar': P.competitif,
  'sand:kanto': P.baigneSable,
  'sand:alola': P.chasseNeige,
  'geodude:kanto': P.fermete,
  'geodude:alola': P.peauElectrik,
  'mewtwo:x': P.impassible,
  'mewtwo:y': P.insomnie,
  'tyrogue:lee': P.temeraire,
  'tyrogue:chan': P.poingDeFer,
  'tyrogue:top': P.technicien,
  'nincada:ninjask': P.turbo,
  'nincada:shedinja': P.gardeMystik,
  'snorunt:glalie': P.attention,
  'snorunt:froslass': P.corpsGel,
  'oshawott:unys': P.torrent,
  'oshawott:hisui': P.tranchant,
  'kyurem:noir': P.teraVoltage,
  'kyurem:blanc': P.turbobrasier,
  'meloetta:chant': P.serenite,
  'meloetta:danse': P.pasDeDanse,
  'bergmite:kalos': P.fermete,
  'bergmite:hisui': P.prognathe,
  'goomy:kalos': P.poisseux,
  'goomy:hisui': P.armurbaston,
  'rockruff:jour': P.regardVif,
  'rockruff:nuit': P.annuleGarde,
  'rockruff:crepuscule': P.griffeDure,
  'lunala:lunala': P.spectroBouclier,
  'lunala:solgaleo': P.metalloGarde,
  'ogerpon:turquoise': P.masque,
  'ogerpon:puits': P.masquePuits,
  'ogerpon:fourneau': P.masqueFourneau,
  'ogerpon:pierre': P.masquePierre,
  'teddiursa:ursaking': P.cran,
  'teddiursa:lune': P.oeilRevelateur,
  'wurmple:charmillon': P.essaim,
  'wurmple:papinox': P.ecranPoudre,
  'koffing:kanto': P.levitation,
  'koffing:galar': P.gazInhibiteur,
  'poliwag:tartard': P.glissade,
  'poliwag:tarpaud': P.crachin,
  'ponyta:kanto': P.corpsArdent,
  'ponyta:galar': P.anticipation,
  'slowpoke:flagadoss': P.regeForce,
  'slowpoke:roigada': P.impassible,
  'clamperl:serpang': P.glissade,
  'clamperl:rosabyss': P.hydratation,
  'kubfu:poing': P.poingInvisibleFinal,
  'kubfu:mille': P.poingInvisibleMille
};
